import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Grid,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Box,
  Button,
  FormHelperText,
  Stack,
  Checkbox,
  ListItemText,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { useForm, Controller } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { userTypeCreateUpdate, userTypeGetById } from '../userType.service';
import { getAllcountryManagement } from '../../../countryManagement/countryManagement.service';
import Loader from '../../../../components/loader';
import AlertToast from '../../../../components/alertToast';
import InlineAlert from '../../../../components/inlineAlert';
import { resultMessage } from '../../../../../service/apiResult';
import {
  createMenuProps,
  FieldLabel,
  formFieldSx,
  helperTextSx,
  placeholderTextSx,
} from '../../../../styles/formStyles';
import { primaryIconColor } from '../../../../styles/iconStyles';
import {
  createEditBackButtonSx,
  createEditCancelButtonSx,
  createEditCardSx,
  createEditFooterSx,
  createEditPageHeaderSx,
  createEditPageShellSx,
  createEditPageSubtitleSx,
  createEditPageTitleSx,
  createEditPrimaryButtonSx,
} from '../../../../styles/pageFormStyles';

const sasToken = process.env.REACT_APP_AZURE_SAS_TOKEN;
const ALERT_DURATION = 2000;
const SIDE_PANEL_FIELD_HEIGHT = 232;

const menuProps = createMenuProps({ sx: { width: 'auto' } });
const selectFieldSx = {
  ...formFieldSx,
  '& .MuiInputBase-input::placeholder': {
    color: '#625B71',
    opacity: 1,
    fontSize: 16,
    lineHeight: '24px',
    letterSpacing: '0.5px',
    fontWeight: 400,
  },
};

// Column lengths of iam.persona_types.name / short_name. Symbols are allowed (persona names such
// as PULSE_ADMIN or "DARE MFI" are matched verbatim by eK Link and the apps), so length is the
// only limit enforced here.
const TYPE_NAME_MAX_LENGTH = 50;
const SHORT_NAME_MAX_LENGTH = 20;

function UserTypeCreation() {
  const location = useLocation();
  const { id } = useParams();
  const type = new URLSearchParams(location.search).get('type');
  const navigate = useNavigate();

  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [alert, setAlert] = useState('');
  const [responseMsg, setResponseMsg] = useState('');
  // A failed save keeps its reason on the form until dismissed; the user still has unsaved input
  // in front of them and needs to know what to correct.
  const [saveError, setSaveError] = useState('');
  const [countryData, setCountryData] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);

  const alertClose = () => {
    setAlert('');
    setOpenAlert(false);
  };

  const userTypeSchema = z.object({
    typeName: z
      .string()
      .trim()
      .min(1, { message: 'User Persona is required' })
      .max(TYPE_NAME_MAX_LENGTH, { message: `User Persona must be at most ${TYPE_NAME_MAX_LENGTH} characters` }),
    shortName: z
      .string()
      .trim()
      .min(1, { message: 'Short Name is required' })
      .max(SHORT_NAME_MAX_LENGTH, { message: `Short Name must be at most ${SHORT_NAME_MAX_LENGTH} characters` }),
    countryIds: z
      .array(z.union([z.string(), z.number()]))
      .min(1, { message: 'At least one country is required' }),
    description: z
      .string()
      .transform((val) => val.replace(/<[^>]*>/g, '').trim())
      .refine((val) => val !== '', {
        message: 'Description is required',
      }),
    appUrl: z
      .string()
      .optional()
      .refine(
        (val) => !val || /^https?:\/\/.+/.test(val),
        { message: 'Must be a valid URL starting with http:// or https://' }
      ),
  });

  const {
    handleSubmit,
    formState: { errors },
    control,
    setValue,
  } = useForm({
    resolver: zodResolver(userTypeSchema),
    defaultValues: {
      typeName: '',
      shortName: '',
      countryIds: [],
      description: '',
      appUrl: '',
    },
  });

  const countriesData = useCallback(async () => {
    getAllcountryManagement().then((res) => {
      if (res?.data?.content) {
        setCountryData(res.data.content);
      }
    });
  }, []);

  const fetchData = useCallback(async () => {
    userTypeGetById(id).then((res) => {
      const userTypeData = res?.data;
      if (!userTypeData) return;

      setValue('typeName', userTypeData?.name || '');
      setValue('shortName', userTypeData?.shortName || '');
      setValue('description', userTypeData?.description || '');
      setValue('countryIds', userTypeData?.countryIds || []);
      setValue('appUrl', userTypeData?.appUrl || '');

      if (userTypeData?.iconUrl) {
        setPreview(`${userTypeData.iconUrl}?${sasToken}`);
      }
    });
  }, [id, setValue]);

  useEffect(() => {
    if (type === 'edit') {
      setEditMode(true);
    }
    countriesData();
  }, [type, countriesData]);

  useEffect(() => {
    if (type === 'edit' && id) {
      fetchData();
    }
  }, [type, id, fetchData]);

  const handleCancel = () => {
    editMode ? navigate(`/userTypeView/${id}?type=edit`) : navigate('/userType');
  };

  const onSubmit = async (data) => {
    try {
      const formData = new FormData();
      formData.append('typeName', data.typeName);
      formData.append('shortName', data.shortName);
      formData.append('description', data.description);
      (data.countryIds || []).forEach((countryId) => formData.append('countryIds', countryId));
      formData.append('appUrl', data.appUrl || '');

      if (selectedFile) {
        formData.append('iconUrl', selectedFile);
      }

      setLoading(true);
      userTypeCreateUpdate(formData, editMode, id).then((res) => {
        setLoading(false);

        if (!res.success) {
          // Stays on the form until dismissed: the save failed, so the user is still sitting on
          // unsaved input and needs the reason in front of them while they correct it. Navigating
          // away or letting the message auto-hide would lose both.
          setSaveError(resultMessage(res));
          return;
        }

        setSaveError('');
        setResponseMsg(resultMessage(res, editMode ? 'User persona updated successfully' : 'User persona created successfully'));
        setAlert('true');
        setOpenAlert(true);
        setTimeout(() => {
          editMode ? navigate(`/userTypeView/${id}?type=edit`) : navigate('/userType');
          alertClose();
        }, ALERT_DURATION);
      });
    } catch (error) {
      console.error('User type creation failed:', error);
    }
  };

  const handleFileChange = (event, onChange) => {
    const file = event.target.files?.[0] || null;
    if (!file) return;

    const allowedTypes = ['image/x-icon', 'image/vnd.microsoft.icon', 'image/png', 'image/jpeg', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      setResponseMsg('Only ICO, PNG, JPEG and JPG files are allowed.');
      setAlert('false');
      setOpenAlert(true);
      setTimeout(() => {
        alertClose();
      }, ALERT_DURATION);
      event.target.value = '';
      onChange(null);
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setResponseMsg('File size exceeds 2 MB. Please choose a smaller file.');
      setAlert('false');
      setOpenAlert(true);
      setTimeout(() => {
        alertClose();
      }, ALERT_DURATION);
      event.target.value = '';
      onChange(null);
      return;
    }

    setSelectedFile(file);
    setPreview(URL.createObjectURL(file));
    onChange(file);
    event.target.value = '';
  };

  return (
    <Box sx={createEditPageShellSx}>
      <Box sx={createEditPageHeaderSx}>
        <Button
          onClick={handleCancel}
          startIcon={<Icon icon="mdi:arrow-left" width="20" height="20" />}
          sx={createEditBackButtonSx}
        >
          Back
        </Button>

        <Typography
          className="app-page-title"
          sx={createEditPageTitleSx}
        >
          {editMode ? 'Update User Persona' : 'Create User Persona'}
        </Typography>
        <Typography sx={createEditPageSubtitleSx}>
          Configure user persona details, access context, and the icon used across the platform.
        </Typography>
      </Box>

      <InlineAlert message={saveError} onClose={() => setSaveError('')} variant="error" />

      <form onSubmit={handleSubmit(onSubmit)} className="material-form">
        <Box sx={createEditCardSx}>
          <Grid container spacing={4} alignItems="stretch">
            <Grid size={{ xs: 12, lg: 6 }} sx={{ display: 'flex' }}>
              <Stack spacing={2.5} sx={{ width: '100%' }}>
                <Box>
                  <FieldLabel label="User Persona" required />
                  <Controller
                    name="typeName"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="Enter user persona"
                        error={!!error}
                        helperText={error ? error.message : ''}
                        inputProps={{ maxLength: TYPE_NAME_MAX_LENGTH }}
                        sx={{ ...formFieldSx, ...helperTextSx }}
                      />
                    )}
                  />
                </Box>

                <Box>
                  <FieldLabel label="Country" required />
                  <FormControl fullWidth error={!!errors.countryIds} sx={helperTextSx}>
                    <Controller
                      name="countryIds"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          multiple
                          displayEmpty
                          value={field.value || []}
                          MenuProps={menuProps}
                          IconComponent={(iconProps) => (
                            <Icon icon="mdi:chevron-down" width="22" height="22" color={primaryIconColor} {...iconProps} />
                          )}
                          sx={selectFieldSx}
                          renderValue={(selected) =>
                            !selected || selected.length === 0
                              ? <Box sx={placeholderTextSx}>Select country</Box>
                              : countryData.filter((item) => selected.includes(item.id)).map((item) => item.name).join(', ')
                          }
                        >
                          {countryData.map((item) => (
                            <MenuItem key={item.id} value={item.id}>
                              <Checkbox checked={(field.value || []).includes(item.id)} />
                              <ListItemText primary={item.name} />
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                    {!!errors.countryIds && <FormHelperText error>{errors.countryIds.message}</FormHelperText>}
                  </FormControl>
                </Box>

                <Box>
                  <FieldLabel label="Description" required />
                  <Controller
                    name="description"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        fullWidth
                        multiline
                        rows={6}
                        placeholder="Write description here..."
                        error={!!error}
                        helperText={error ? error.message : ''}
                        sx={{ ...formFieldSx, ...helperTextSx }}
                      />
                    )}
                  />
                </Box>
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, lg: 6 }} sx={{ display: 'flex' }}>
              <Stack spacing={2.5} sx={{ width: '100%' }}>
                <Box>
                  <FieldLabel label="Short Name" required />
                  <Controller
                    name="shortName"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="Enter short name"
                        error={!!error}
                        helperText={error ? error.message : ''}
                        inputProps={{ maxLength: SHORT_NAME_MAX_LENGTH }}
                        sx={{ ...formFieldSx, ...helperTextSx }}
                      />
                    )}
                  />
                </Box>

                <Box>
                  <FieldLabel label="Redirection URL" />
                  <Controller
                    name="appUrl"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="e.g., https://example.com"
                        error={!!error}
                        helperText={error ? error.message : ''}
                        sx={{ ...formFieldSx, ...helperTextSx }}
                      />
                    )}
                  />
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                  <FieldLabel label="Persona Icon" />
                  <Controller
                    name="iconUpload"
                    control={control}
                    defaultValue=""
                    render={({ field: { onChange }, fieldState: { error } }) => (
                      <>
                        <Box
                          sx={{
                            flex: 1,
                            minHeight: SIDE_PANEL_FIELD_HEIGHT,
                            border: '1px dashed #D9E2F4',
                            borderRadius: '12px',
                            backgroundColor: '#FFFFFF',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center',
                            p: 3,
                            position: 'relative',
                            boxSizing: 'border-box',
                          }}
                        >
                          {preview ? (
                            <Box
                              sx={{
                                position: 'relative',
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <Box component="img" src={preview} alt="Preview" sx={{ maxWidth: '100%', maxHeight: '100%', borderRadius: '12px' }} />
                              <Button
                                onClick={(event) => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  setSelectedFile(null);
                                  setPreview(null);
                                  onChange(null);
                                }}
                                sx={{
                                  minWidth: 'auto',
                                  position: 'absolute',
                                  top: -12,
                                  right: -12,
                                  width: 32,
                                  height: 32,
                                  borderRadius: '50%',
                                  bgcolor: '#FFFFFF',
                                  border: '1px solid #D9E2F4',
                                  color: '#DA342E',
                                  p: 0,
                                }}
                              >
                                <Icon icon="mdi:close" width="18" height="18" />
                              </Button>
                            </Box>
                          ) : (
                            <Stack spacing={1.5} alignItems="center">
                              <Box
                                sx={{
                                  width: 60,
                                  height: 60,
                                  borderRadius: '50%',
                                  border: '2px solid #76A312',
                                  color: '#76A312',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <Icon icon="material-symbols:upload-rounded" width="32" height="32" />
                              </Box>
                              <Typography sx={{ fontSize: 14, lineHeight: '20px', letterSpacing: '0.1px', color: '#6C6A86' }}>
                                Supported formats: ICO, PNG, JPEG (Max 2 MB)
                              </Typography>
                              <Button
                                onClick={() => document.getElementById('user-type-icon-input')?.click()}
                                sx={{
                                  minWidth: { xs: '100%', sm: 180 },
                                  maxWidth: 240,
                                  height: 40,
                                  borderRadius: '10px',
                                  border: '1px solid #76A312',
                                  color: '#4F8506',
                                  textTransform: 'none',
                                  fontSize: 16,
                                  lineHeight: '24px',
                                  letterSpacing: '0.15px',
                                  fontWeight: 500,
                                }}
                              >
                                Upload Icon
                              </Button>
                            </Stack>
                          )}
                        </Box>
                        <input hidden type="file" id="user-type-icon-input" accept=".ico,.png,.jpeg,.jpg" onChange={(event) => handleFileChange(event, onChange)} />
                        {error && <Typography variant="body2" color="error" mt={1}>{error.message}</Typography>}
                      </>
                    )}
                  />
                </Box>
              </Stack>
            </Grid>
          </Grid>

          <Box sx={{ ...createEditFooterSx, flexDirection: { xs: 'column-reverse', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' } }}>
            <Box sx={{ width: { xs: '100%', sm: 'auto' }, display: 'flex', justifyContent: { xs: 'stretch', sm: 'flex-start' } }}>
              <Button
                onClick={handleCancel}
                sx={{
                  ...createEditCancelButtonSx,
                  width: { xs: '100%', sm: 'auto' },
                  justifyContent: { xs: 'center', sm: 'flex-start' },
                }}
              >
                Cancel
              </Button>
            </Box>
            <Box sx={{ width: { xs: '100%', sm: 'auto' }, display: 'flex', justifyContent: { xs: 'stretch', sm: 'flex-end' }, ml: { sm: 'auto' } }}>
              <Button
                variant="contained"
                type="submit"
                sx={{ ...createEditPrimaryButtonSx, minWidth: { sm: 170 }, width: { xs: '100%', sm: 'auto' } }}
              >
                {editMode ? 'Update User Persona' : 'Create User Persona'}
              </Button>
            </Box>
          </Box>
        </Box>
      </form>

      {loading && <Loader />}

      <AlertToast open={openAlert && alert === 'true'} onClose={alertClose} message={responseMsg} variant="success" />
      <AlertToast open={openAlert && alert === 'false'} onClose={alertClose} message={responseMsg} variant="error" />
    </Box>
  );
}

export default UserTypeCreation;
